import { useState, useEffect, useCallback } from 'react';
import Badge from '../common/Badge';
import Button from '../common/Button';
import RoundRobinSimulator from './RoundRobinSimulator';
import CompleteBetSlipModal from './CompleteBetSlipModal';
import betslipService from '../../services/betslipService';

/**
 * BetSlipCard - Displays a single betslip with actions.
 *
 * @param {Object} betslip - The betslip data
 * @param {Function} onClick - Click handler for the card
 * @param {Function} onUpdate - Callback when betslip is updated
 * @param {Function} onSaveLegResults - Optional custom save function (DIP). Defaults to betslipService.
 */
const BetSlipCard = ({ betslip, onClick, onUpdate, onSaveLegResults }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);

  // Fetch thumbnail via API (not directly from Azure)
  useEffect(() => {
    let blobUrl = null;

    if (betslip.thumbnail_url && betslip.id) {
      betslipService.fetchThumbnail(betslip.id)
        .then(url => {
          blobUrl = url;
          setThumbnailUrl(url);
        })
        .catch(err => {
          console.error('Failed to load thumbnail:', err);
          setThumbnailUrl(null);
        });
    }

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [betslip.id, betslip.thumbnail_url]);

  // Status badge configuration
  const statusConfig = {
    pending: { variant: 'warning', label: 'Pending', icon: '⏳' },
    won: { variant: 'success', label: 'Won', icon: '✅' },
    lost: { variant: 'error', label: 'Lost', icon: '❌' },
    pushed: { variant: 'info', label: 'Push', icon: '↔️' },
    cancelled: { variant: 'default', label: 'Cancelled', icon: '🚫' },
  };

  const status = statusConfig[betslip.status] || statusConfig.pending;

  // Check if betslip is pending and more than 1 day old
  const isPending = betslip.status === 'pending';
  const betDate = new Date(betslip.bet_date);
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const isOlderThanOneDay = betDate < oneDayAgo;
  const showCompleteButton = isPending && isOlderThanOneDay;

  // Handle completion of betslip
  const handleComplete = (updatedBetslip) => {
    // Notify parent component to refresh the betslip data
    onUpdate?.(updatedBetslip);
  };

  // Save handler with dependency injection - uses custom function or defaults to service
  const handleSaveLegResults = useCallback(async (betslipId, legUpdates) => {
    if (onSaveLegResults) {
      return onSaveLegResults(betslipId, legUpdates);
    }
    // Default to betslipService if no custom handler provided
    return betslipService.updateLegResults(betslipId, legUpdates);
  }, [onSaveLegResults]);

  // Calculate potential profit
  const potentialProfit = betslip.potential_payout
    ? (betslip.potential_payout - betslip.stake_amount).toFixed(2)
    : null;

  // Calculate actual profit/loss
  const actualProfitLoss = betslip.actual_payout
    ? (betslip.actual_payout - betslip.stake_amount).toFixed(2)
    : null;

  // Format date
  const formattedDate = new Date(betslip.bet_date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div
      onClick={onClick}
      className="glass-card p-4 hover:bg-bg-card-hover cursor-pointer transition-all duration-200 border border-border hover:border-accent-blue"
    >
      <div className="flex gap-4">
        {/* Thumbnail (if uploaded) - fetched via API */}
        {betslip.thumbnail_url && (
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-bg-primary rounded-lg overflow-hidden border border-border">
              {!thumbnailUrl && (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="animate-spin h-6 w-6 border-2 border-accent-blue border-t-transparent rounded-full"></div>
                </div>
              )}
              {thumbnailUrl && (
                <img
                  src={thumbnailUrl}
                  alt="Bet slip thumbnail"
                  className={`w-full h-full object-cover ${imageLoaded ? 'block' : 'hidden'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoaded(false)}
                />
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-text-primary">
                  {betslip.betslip_type === 'round_robin'
                    ? `${betslip.legs?.length || 0}-Pick ${betslip.rr_legs_per_parlay}'s Round Robin`
                    : betslip.betslip_type === 'parlay'
                    ? `${betslip.legs?.length || 0}-Leg Parlay`
                    : betslip.betslip_type === 'same_game_parlay'
                    ? 'Same Game Parlay'
                    : 'Single Bet'}
                </h3>
                <Badge variant={status.variant} size="sm">
                  {status.icon} {status.label}
                </Badge>
                {betslip.betslip_type === 'round_robin' && (
                  <Badge variant="info" size="sm">
                    {betslip.rr_num_combinations} Combos
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-text-secondary flex-wrap">
                <span>{betslip.sportsbook}</span>
                <span>•</span>
                <span>{formattedDate}</span>
                <span>•</span>
                <span className="capitalize">{betslip.entry_method}</span>
                {betslip.source && (
                  <>
                    <span>•</span>
                    {betslip.source_url ? (
                      <a
                        href={betslip.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-accent-blue hover:text-accent-blue/80"
                      >
                        {betslip.source}
                      </a>
                    ) : (
                      <span className="text-accent-blue">{betslip.source}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Bet Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            {/* Stake */}
            <div>
              <p className="text-xs text-text-secondary mb-1">Stake</p>
              <p className="text-sm font-semibold text-text-primary">
                ${betslip.stake_amount?.toFixed(2) || '0.00'}
              </p>
            </div>

            {/* Odds */}
            {betslip.total_odds && (
              <div>
                <p className="text-xs text-text-secondary mb-1">Odds</p>
                <p className="text-sm font-semibold text-text-primary">
                  {betslip.total_odds > 0 ? '+' : ''}{betslip.total_odds}
                </p>
              </div>
            )}

            {/* Potential Payout */}
            {betslip.potential_payout && (
              <div>
                <p className="text-xs text-text-secondary mb-1">Potential Win</p>
                <p className="text-sm font-semibold text-accent-green">
                  ${betslip.potential_payout.toFixed(2)}
                  {potentialProfit && (
                    <span className="text-xs ml-1">(+${potentialProfit})</span>
                  )}
                </p>
              </div>
            )}

            {/* Actual Payout (if settled) */}
            {betslip.actual_payout !== null && betslip.actual_payout !== undefined && (
              <div>
                <p className="text-xs text-text-secondary mb-1">Result</p>
                <p className={`text-sm font-semibold ${
                  actualProfitLoss > 0 ? 'text-accent-green' :
                  actualProfitLoss < 0 ? 'text-accent-red' :
                  'text-text-secondary'
                }`}>
                  ${betslip.actual_payout.toFixed(2)}
                  {actualProfitLoss !== null && (
                    <span className="text-xs ml-1">
                      ({actualProfitLoss > 0 ? '+' : ''}${actualProfitLoss})
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Legs Preview */}
          {betslip.legs && betslip.legs.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetails(!showDetails);
                }}
                className="text-xs text-accent-blue hover:text-accent-blue/80 transition-colors mb-2"
              >
                {showDetails ? '▼' : '▶'} {betslip.legs.length} Leg{betslip.legs.length > 1 ? 's' : ''}
              </button>

              {showDetails ? (
                <div className="space-y-2 mt-2">
                  {betslip.legs.map((leg, index) => (
                    <div key={index} className="text-xs text-text-secondary p-2 bg-bg-primary rounded border border-border">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-text-primary">
                          {leg.player_name || `${leg.away_team_name} @ ${leg.home_team_name}`}
                        </span>
                        {leg.result && (
                          <Badge variant={leg.result === 'won' ? 'success' : leg.result === 'lost' ? 'error' : 'warning'} size="sm">
                            {leg.result}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="capitalize">{leg.bet_type}</span>
                        {leg.line_value && (
                          <>
                            <span>•</span>
                            <span>{leg.selection} {leg.line_value}</span>
                          </>
                        )}
                        {leg.odds && (
                          <>
                            <span>•</span>
                            <span>{leg.odds > 0 ? '+' : ''}{leg.odds}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {betslip.legs.slice(0, 2).map((leg, index) => (
                    <div key={index} className="text-xs text-text-secondary flex items-center gap-2">
                      <span className="font-medium text-text-primary">
                        {leg.player_name || `${leg.away_team_name} @ ${leg.home_team_name}`}
                      </span>
                      <span>•</span>
                      <span>{leg.bet_type}</span>
                      {leg.line_value && (
                        <>
                          <span>•</span>
                          <span>{leg.selection} {leg.line_value}</span>
                        </>
                      )}
                    </div>
                  ))}
                  {betslip.legs.length > 2 && (
                    <p className="text-xs text-text-secondary italic">
                      +{betslip.legs.length - 2} more...
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {betslip.notes && (
            <div className="mt-2 text-xs text-text-secondary italic">
              "{betslip.notes}"
            </div>
          )}

          {/* Round Robin Simulator Button */}
          {betslip.betslip_type === 'round_robin' && (
            <div className="mt-3 pt-3 border-t border-border">
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSimulator(true);
                }}
                className="w-full"
              >
                🎲 Simulate Payout
              </Button>
            </div>
          )}

          {/* Complete Button for pending betslips > 1 day old */}
          {showCompleteButton && (
            <div className={`mt-3 pt-3 ${betslip.betslip_type !== 'round_robin' ? 'border-t border-border' : ''}`}>
              <Button
                variant="warning"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCompleteModal(true);
                }}
                className="w-full"
              >
                Complete Manually
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Round Robin Simulator Modal */}
      <RoundRobinSimulator
        betslip={betslip}
        isOpen={showSimulator}
        onClose={() => setShowSimulator(false)}
      />

      {/* Complete BetSlip Modal */}
      <CompleteBetSlipModal
        betslip={betslip}
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        onSave={handleSaveLegResults}
        onComplete={handleComplete}
      />
    </div>
  );
};

export default BetSlipCard;
